import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import ModernTimeDropdown from "./modernDropdown";
import AdvancedFilterDropdown from "./AdvancedFilterDropdown";
import ModernDateRangePicker from "./ModernDateRangePicker";
import {
  clearCrmLeadCreatedAtFields,
  formatCrmLeadCreatedAtChipLabel,
  sanitizeCrmLeadCreatedAtForApply,
  setCrmLeadCreatedAtPreset,
  CRM_LEAD_CREATED_AT_FILTER_KEYS,
} from "../../utils/filterUtils";

export default function CallAdvancedFiltersPanel({
  isOpen,
  onClose,
  multiSelectFields = [],
  advancedFilters,
  setAdvancedFilters,
  handleAdvancedInputChange,
  handleNumberInputChange,
  dummyAdvancedFilterData,
  expandedSections,
  setExpandedSections,
  updateAgentFeedback,
  updateBusinessInsights,
  getBusinessInsightKeys,
  getBusinessInsightValues,
  handleConcernCategoryChange,
  getConcernCategoryKeys,
  getConcernSubcategoriesForCategory,
  handleQueryCategoryChange,
  getQueryCategoryKeys,
  getQuerySubcategoriesForCategory,
  handleDispositionChange,
  getDispositionKeys,
  getSubdispositionsForDisposition,
  handleCustomModuleParameterChange,
  getCustomModuleKeys,
  getCustomModuleValues,
  showCampaignBatchId = false,
  showNeedsReview = false,
  crmStageKey = "crm_lead_stage",
  crmSubStageKey = "crm_lead_sub_stage",
  actorTypeLabel = "Actor Type",
}) {
  const getInitialCustomModuleRows = (value = {}) => {
    if (Array.isArray(value.custom_module_multiple)) return value.custom_module_multiple;
    if (
      value.custom_module_parameter_name &&
      value.custom_module_parameter_answer !== null &&
      value.custom_module_parameter_answer !== undefined &&
      value.custom_module_parameter_answer !== ""
    ) {
      return [
        {
          custom_module_parameter_name: value.custom_module_parameter_name,
          custom_module_parameter_answer: value.custom_module_parameter_answer,
        },
      ];
    }
    return [];
  };

  const getInitialAgentFeedbackRows = (value = {}) => {
    if (Array.isArray(value.agent_feedback_multiple) && value.agent_feedback_multiple.length > 0) {
      return value.agent_feedback_multiple;
    }
    return [
      {
        agent_feedback_parameter: "",
        agent_feedback_parameter_score_lower: null,
        agent_feedback_parameter_score_upper: null,
      },
    ];
  };

  const getInitialBusinessInsightRows = (value = {}) => {
    if (Array.isArray(value.business_insights_multiple) && value.business_insights_multiple.length > 0) {
      return value.business_insights_multiple;
    }
    return [{ business_insights_parameter: "", business_insights_parameter_value: null }];
  };

  const withNormalizedCustomModuleFilters = (value = {}) => ({
    ...value,
    agent_feedback_multiple: getInitialAgentFeedbackRows(value),
    business_insights_multiple: getInitialBusinessInsightRows(value),
    custom_module_multiple: (() => {
      const rows = getInitialCustomModuleRows(value);
      if (rows.length > 0) return rows;
      return [{ custom_module_parameter_name: "", custom_module_parameter_answer: "" }];
    })(),
  });

  const cloneFilters = (value) => JSON.parse(JSON.stringify(value || {}));
  const getClearedFilters = (value) =>
    Object.keys(value || {}).reduce((acc, key) => {
      const current = value[key];
      if (Array.isArray(current)) acc[key] = [];
      else if (typeof current === "number" || current === null) acc[key] = null;
      else acc[key] = "";
      return acc;
    }, {});

  const [localFilters, setLocalFilters] = useState(withNormalizedCustomModuleFilters(cloneFilters(advancedFilters)));
  const [activeTab, setActiveTab] = useState("standard");

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(withNormalizedCustomModuleFilters(cloneFilters(advancedFilters)));
      setActiveTab("standard");
    }
  }, [isOpen, advancedFilters]);

  if (!isOpen) return null;

  const setField = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const normalizeSelectionArray = (value) => {
    if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && item !== "");
    if (value === null || value === undefined || value === "") return [];
    return [value];
  };

  const hasSelection = (value) => normalizeSelectionArray(value).length > 0;

  const getMergedDependentOptions = (selectedValues, getOptionsForOneParent) => {
    const parentValues = normalizeSelectionArray(selectedValues);
    if (parentValues.length === 0) {
      const fallback = getOptionsForOneParent(selectedValues);
      return Array.isArray(fallback) ? fallback : [];
    }
    const merged = [];
    parentValues.forEach((parent) => {
      const options = getOptionsForOneParent(parent);
      if (Array.isArray(options)) {
        options.forEach((option) => {
          if (!merged.includes(option)) merged.push(option);
        });
      }
    });
    return merged;
  };

  const formatChipValue = (value) => {
    if (Array.isArray(value)) {
      const cleaned = value.filter((item) => item !== null && item !== undefined && item !== "");
      if (cleaned.length <= 2) return cleaned.join(", ");
      return `${cleaned.slice(0, 2).join(", ")} +${cleaned.length - 2} more`;
    }
    if (value && typeof value === "object") {
      const lower = value.lower ?? "";
      const upper = value.upper ?? "";
      if (lower !== "" || upper !== "") return `${lower} to ${upper}`;
    }
    return value;
  };

  const normalizeDataType = (dataType) => String(dataType || "").trim().toLowerCase();
  const isDropdownDataType = (dataType) => {
    const normalized = normalizeDataType(dataType);
    return (
      normalized === "yes/no/na" ||
      normalized === "categorical" ||
      normalized === "categorical - multiple values"
    );
  };
  const isNumberDataType = (dataType) => normalizeDataType(dataType) === "number";
  const isTextDataType = (dataType) => {
    const normalized = normalizeDataType(dataType);
    return normalized === "" || normalized === "text" || normalized === "null";
  };

  const getMetadataEntries = (source) => {
    if (!Array.isArray(source)) return [];
    return source
      .filter((row) => Array.isArray(row) && row.length > 0)
      .map(([key, options, dataType]) => ({
        key: String(key || ""),
        options: Array.isArray(options) ? options : [],
        dataType,
      }))
      .filter((item) => item.key);
  };

  const normalizeParameterKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");

  const getModuleParameterMeta = (moduleType, selectedParameter) => {
    const businessInsightsSource =
      dummyAdvancedFilterData.business_insights_parameters || dummyAdvancedFilterData.business_insight_parameters;
    const source = moduleType === "business_insight" ? businessInsightsSource : dummyAdvancedFilterData.custom_module;
    const entries = getMetadataEntries(source);
    const selectedParams = normalizeSelectionArray(selectedParameter);
    const normalizedSelectedParams = selectedParams.map(normalizeParameterKey);
    const matched = entries.filter((entry) =>
      normalizedSelectedParams.includes(normalizeParameterKey(entry.key))
    );
    const target =
      matched.length > 0
        ? matched
        : entries.filter(
            (entry) => normalizeParameterKey(entry.key) === normalizeParameterKey(selectedParameter)
          );

    const mergedOptions = [];
    target.forEach((entry) => {
      entry.options.forEach((option) => {
        if (!mergedOptions.includes(option)) mergedOptions.push(option);
      });
    });

    const dataType = target.find((entry) => entry.dataType !== null && entry.dataType !== undefined)?.dataType ?? null;
    return { dataType, options: mergedOptions };
  };

  const getDefaultOptionsByType = (dataType) => {
    const normalized = normalizeDataType(dataType);
    if (normalized === "yes/no/na") return ["Yes", "No", "NA"];
    return [];
  };

  const getValueInputKind = (dataType) => {
    if (isNumberDataType(dataType)) return "number";
    if (isDropdownDataType(dataType)) return "dropdown";
    if (isTextDataType(dataType)) return "text";
    return "text";
  };

  const getValueResetForType = (dataType) => {
    const inputKind = getValueInputKind(dataType);
    if (inputKind === "number") return { lower: "", upper: "" };
    if (inputKind === "dropdown") return "";
    return "";
  };

  const handleApply = () => {
    const firstValidCustomModuleRow = (localFilters.custom_module_multiple || []).find(
      (item) =>
        item?.custom_module_parameter_name &&
        item?.custom_module_parameter_answer !== null &&
        item?.custom_module_parameter_answer !== undefined &&
        item?.custom_module_parameter_answer !== ""
    );
    const sanitized = sanitizeCrmLeadCreatedAtForApply(localFilters);
    setAdvancedFilters({
      ...sanitized,
      custom_module_parameter_name: firstValidCustomModuleRow?.custom_module_parameter_name || "",
      custom_module_parameter_answer: firstValidCustomModuleRow?.custom_module_parameter_answer || "",
    });
    onClose();
  };

  const handleClear = () => {
    setLocalFilters(withNormalizedCustomModuleFilters(getClearedFilters(localFilters)));
  };

  const updateAgentFeedbackLocal = (index, key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      agent_feedback_multiple: (prev.agent_feedback_multiple || []).map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const updateBusinessInsightsLocal = (index, key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      business_insights_multiple: (prev.business_insights_multiple || []).map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const handleBusinessInsightParameterChange = (index, value) => {
    const { dataType } = getModuleParameterMeta("business_insight", value);
    setLocalFilters((prev) => ({
      ...prev,
      business_insights_multiple: (prev.business_insights_multiple || []).map((item, i) =>
        i === index
          ? {
              ...item,
              business_insights_parameter: value,
              business_insights_parameter_value: getValueResetForType(dataType),
            }
          : item
      ),
    }));
  };

  const removeAgentFeedbackRow = (index) => {
    setLocalFilters((prev) => ({
      ...prev,
      agent_feedback_multiple: (prev.agent_feedback_multiple || []).filter((_, i) => i !== index),
    }));
  };

  const addAgentFeedbackRow = () => {
    setLocalFilters((prev) => ({
      ...prev,
      agent_feedback_multiple: [
        ...(prev.agent_feedback_multiple || []),
        {
          agent_feedback_parameter: "",
          agent_feedback_parameter_score_lower: null,
          agent_feedback_parameter_score_upper: null,
        },
      ],
    }));
  };

  const removeBusinessInsightRow = (index) => {
    setLocalFilters((prev) => ({
      ...prev,
      business_insights_multiple: (prev.business_insights_multiple || []).filter((_, i) => i !== index),
    }));
  };

  const addBusinessInsightRow = () => {
    setLocalFilters((prev) => ({
      ...prev,
      business_insights_multiple: [
        ...(prev.business_insights_multiple || []),
        { business_insights_parameter: "", business_insights_parameter_value: null },
      ],
    }));
  };

  const updateCustomModuleLocal = (index, key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      custom_module_multiple: (prev.custom_module_multiple || []).map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const handleCustomModuleParameterChangeLocal = (index, value) => {
    const { dataType } = getModuleParameterMeta("custom_module", value);
    setLocalFilters((prev) => ({
      ...prev,
      custom_module_multiple: (prev.custom_module_multiple || []).map((item, i) =>
        i === index
          ? {
              ...item,
              custom_module_parameter_name: value,
              custom_module_parameter_answer: getValueResetForType(dataType),
            }
          : item
      ),
    }));
  };

  const removeCustomModuleRow = (index) => {
    setLocalFilters((prev) => ({
      ...prev,
      custom_module_multiple: (prev.custom_module_multiple || []).filter((_, i) => i !== index),
    }));
  };

  const addCustomModuleRow = () => {
    setLocalFilters((prev) => ({
      ...prev,
      custom_module_multiple: [
        ...(prev.custom_module_multiple || []),
        { custom_module_parameter_name: "", custom_module_parameter_answer: "" },
      ],
    }));
  };

  const tabs = [
    { id: "standard", label: "Standard" },
    { id: "agent_feedback", label: "Agent Feedback" },
    { id: "business_insights", label: "Business Insight" },
    { id: "lead_quality", label: "Lead Quality" },
    { id: "disposition", label: "Disposition & Subdisposition" },
    { id: "concerns", label: "Concerns & Queries" },
    { id: "custom_module", label: "Custom Module" },
  ];

  const crmStageAlias = crmStageKey === "lead_stage" ? ["crm_lead_stage"] : ["lead_stage"];
  const crmSubStageAlias = crmSubStageKey === "lead_sub_stage" ? ["crm_lead_sub_stage"] : ["lead_sub_stage"];
  const datePresetOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "This Month",
    "Last Month",
    "Last 7 Days",
    "Last 30 Days",
    "Last 3 Months",
    "Custom Range",
  ];
  const chipExcludedKeys = new Set([
    "agent_feedback_multiple",
    "business_insights_multiple",
    "custom_module_multiple",
    "custom_module_parameter_name",
    "custom_module_parameter_answer",
  ]);

  const formatFilterLabel = (key) =>
    String(key || "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const clearScalarField = (key) => {
    setLocalFilters((prev) => {
      const next = { ...prev };
      if (key === "concernCategory") {
        next.concernCategory = "";
        next.concernSubcategory = "";
      } else if (key === "queryCategory") {
        next.queryCategory = "";
        next.querySubcategory = "";
      } else if (key === "disposition") {
        next.disposition = "";
        next.subdisposition = "";
      } else if (key === "crm_lead_created_at" || CRM_LEAD_CREATED_AT_FILTER_KEYS.includes(key)) {
        Object.assign(next, clearCrmLeadCreatedAtFields(next));
      } else if (Array.isArray(next[key])) {
        next[key] = [];
      } else if (key === "scoreLower" || key === "scoreUpper") {
        next[key] = null;
      } else if (typeof next[key] === "number" || next[key] === null) {
        next[key] = null;
      } else {
        next[key] = "";
      }
      return next;
    });
  };

  const selectedFilterChips = [];
  Object.entries(localFilters || {}).forEach(([key, value]) => {
    if (chipExcludedKeys.has(key)) return;
    if (
      key === "crm_lead_created_at_range_type" ||
      CRM_LEAD_CREATED_AT_FILTER_KEYS.includes(key)
    ) {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0 || typeof value[0] === "object") return;
      const cleanedValues = value.filter((item) => item !== null && item !== undefined && item !== "");
      if (cleanedValues.length === 0) return;
      selectedFilterChips.push({
        id: `${key}-multi`,
        label: `${formatFilterLabel(key)}: ${formatChipValue(cleanedValues)}`,
        onRemove: () => clearScalarField(key),
      });
      return;
    }
    if (value === null || value === undefined || value === "") return;
    if (typeof value === "object") return;
    selectedFilterChips.push({
      id: `${key}-${String(value)}`,
      label: `${formatFilterLabel(key)}: ${value}`,
      onRemove: () => clearScalarField(key),
    });
  });
  const crmLeadCreatedAtChipLabel = formatCrmLeadCreatedAtChipLabel(localFilters);
  if (crmLeadCreatedAtChipLabel) {
    selectedFilterChips.push({
      id: "crm-lead-created-at-range",
      label: crmLeadCreatedAtChipLabel,
      onRemove: () => setLocalFilters((prev) => clearCrmLeadCreatedAtFields(prev)),
    });
  }

  (localFilters.agent_feedback_multiple || []).forEach((item, index) => {
    const param = item?.agent_feedback_parameter;
    const lower = item?.agent_feedback_parameter_score_lower;
    const upper = item?.agent_feedback_parameter_score_upper;
    if (!param && (lower === null || lower === undefined || lower === "") && (upper === null || upper === undefined || upper === "")) return;
    const scoreText =
      lower !== null && lower !== undefined && lower !== "" && upper !== null && upper !== undefined && upper !== ""
        ? ` (${lower}-${upper})`
        : "";
    selectedFilterChips.push({
      id: `agent-feedback-${index}`,
      label: `Agent Feedback: ${param || "Score"}${scoreText}`,
      onRemove: () => {
        updateAgentFeedbackLocal(index, "agent_feedback_parameter", "");
        updateAgentFeedbackLocal(index, "agent_feedback_parameter_score_lower", null);
        updateAgentFeedbackLocal(index, "agent_feedback_parameter_score_upper", null);
      },
    });
  });

  (localFilters.business_insights_multiple || []).forEach((item, index) => {
    const param = item?.business_insights_parameter;
    const value = item?.business_insights_parameter_value;
    if (!hasSelection(param) && !hasSelection(value)) return;
    selectedFilterChips.push({
      id: `business-insight-${index}`,
      label: `Business Insight: ${hasSelection(param) ? formatChipValue(param) : "Parameter"}${hasSelection(value) ? ` = ${formatChipValue(value)}` : ""}`,
      onRemove: () => {
        updateBusinessInsightsLocal(index, "business_insights_parameter", "");
        updateBusinessInsightsLocal(index, "business_insights_parameter_value", null);
      },
    });
  });

  (localFilters.custom_module_multiple || []).forEach((item, index) => {
    const param = item?.custom_module_parameter_name;
    const value = item?.custom_module_parameter_answer;
    if (!hasSelection(param) && !hasSelection(value)) return;
    selectedFilterChips.push({
      id: `custom-module-${index}`,
      label: `Custom Module: ${hasSelection(param) ? formatChipValue(param) : "Parameter"}${hasSelection(value) ? ` = ${formatChipValue(value)}` : ""}`,
      onRemove: () => {
        updateCustomModuleLocal(index, "custom_module_parameter_name", "");
        updateCustomModuleLocal(index, "custom_module_parameter_answer", "");
      },
    });
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-start justify-center pt-7" style={{ zIndex: "99999999999999", backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
      <div className="bg-white rounded-lg shadow-lg border w-[96vw] max-w-10xl h-[93vh] flex flex-col" style={{borderRadius: "10px"}}>
        <div className="bg-gray-50 border-b px-4 py-4 flex items-center justify-between" style={{borderRadius: "10px 10px 0 0"}}>
          <h2 className="text-xl font-semibold text-gray-900">Advanced Filters</h2>
          <button className="p-2" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="border-b px-4 py-2 flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap ${activeTab === tab.id ? "bg-indigo-100 text-indigo-700" : "text-gray-600 bg-gray-100"}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "standard" && <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Customer ID</label>
            <input
              type="text"
              maxLength={500}
              placeholder="Enter Customer ID"
              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
              value={localFilters.customerId}
              onChange={(e) => setField("customerId", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="type"
              width="w-full"
              placeholder="Select Type"
              value={localFilters.type}
              onChange={(e) => setField("type", e.target.value)}
              options={["Proper Conversation", "Non-Meaningful", "Not Connected"]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Conversation Type Reason</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="conversation_type_reason"
              width="w-full"
              placeholder="Select Type Reason"
              value={localFilters.conversation_type_reason}
              onChange={(e) => setField("conversation_type_reason", e.target.value)}
              options={dummyAdvancedFilterData.conversation_type_reason}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Conversation Direction</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="conversation_direction"
              width="w-full"
              placeholder="Select Direction"
              value={localFilters.conversation_direction}
              onChange={(e) => setField("conversation_direction", e.target.value)}
              options={["Inbound", "Outbound"]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">First Proper Conversation</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="first_proper_conversation"
              width="w-full"
              placeholder="Select Option"
              value={localFilters.first_proper_conversation}
              onChange={(e) => setField("first_proper_conversation", e.target.value)}
              options={["Yes", "No"]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Lead Source</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="lead_source"
              width="w-full"
              placeholder="Select Source"
              value={localFilters.lead_source}
              onChange={(e) => setField("lead_source", e.target.value)}
              options={dummyAdvancedFilterData.lead_sources}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Primary Language</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="primary_language"
              width="w-full"
              placeholder="Select Primary Language"
              value={localFilters.primary_language}
              onChange={(e) => setField("primary_language", e.target.value)}
              options={dummyAdvancedFilterData.primary_languages || []}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Secondary Languages</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="secondary_languages"
              width="w-full"
              placeholder="Select Secondary Languages"
              value={localFilters.secondary_languages}
              onChange={(e) => setField("secondary_languages", e.target.value)}
              options={dummyAdvancedFilterData.secondary_languages || []}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Lead Sub Source</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="lead_sub_source"
              width="w-full"
              placeholder="Select Lead Sub Source"
              value={localFilters.lead_sub_source}
              onChange={(e) => setField("lead_sub_source", e.target.value)}
              options={dummyAdvancedFilterData.lead_sub_sources}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Customer Phone Number</label>
            <input
              type="text"
              maxLength={500}
              placeholder="Enter Customer Phone Number"
              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
              value={localFilters.customer_phone_number}
              onChange={(e) => setField("customer_phone_number", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CRM Location</label>
            <input
              type="text"
              maxLength={500}
              placeholder="Enter CRM Location"
              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
              value={localFilters.crm_location}
              onChange={(e) => setField("crm_location", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Campaign ID</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="campaignId"
              width="w-full"
              placeholder="Select Campaign ID"
              value={localFilters.campaignId}
              onChange={(e) => setField("campaignId", e.target.value)}
              options={dummyAdvancedFilterData.campaign_ids}
            />
          </div>
          {showCampaignBatchId && (
            <div>
              <label className="block text-sm font-medium mb-2">Campaign Batch ID</label>
              <AdvancedFilterDropdown
                multiSelectFields={multiSelectFields}
                fieldKey="campaignBatchId"
                width="w-full"
                placeholder="Select Campaign Batch ID"
                value={localFilters.campaignBatchId}
                onChange={(e) => setField("campaignBatchId", e.target.value)}
                options={dummyAdvancedFilterData.campaign_batch_ids}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">{actorTypeLabel}</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="actorType"
              width="w-full"
              placeholder={`Select ${actorTypeLabel}`}
              value={localFilters.actorType}
              onChange={(e) => setField("actorType", e.target.value)}
              options={["Human Agent", "AI Agent"]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CRM Lead Stage</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey={crmStageKey}
              aliasKeys={crmStageAlias}
              width="w-full"
              placeholder="Select CRM Lead Stage"
              value={localFilters[crmStageKey]}
              onChange={(e) => setField(crmStageKey, e.target.value)}
              options={dummyAdvancedFilterData.lead_stages}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CRM Lead Sub Stage</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey={crmSubStageKey}
              aliasKeys={crmSubStageAlias}
              width="w-full"
              placeholder="Select CRM Lead Sub Stage"
              value={localFilters[crmSubStageKey]}
              onChange={(e) => setField(crmSubStageKey, e.target.value)}
              options={dummyAdvancedFilterData.lead_sub_stages}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CRM Lead Created At</label>
            <ModernTimeDropdown
              width="w-full"
              placeholder="Select CRM Lead Created At"
              value={localFilters.crm_lead_created_at}
              onChange={(e) => setLocalFilters((prev) => setCrmLeadCreatedAtPreset(prev, e.target.value))}
              options={datePresetOptions}
            />
            {localFilters.crm_lead_created_at === "Custom Range" && (
              <div className="mt-2">
                <ModernDateRangePicker
                  startDate={localFilters.crm_lead_created_at_start}
                  endDate={localFilters.crm_lead_created_at_end}
                  rangeType={localFilters.crm_lead_created_at_range_type || "between"}
                  maxRangeDays={90}
                  placeholder="Select CRM Lead Created At Range"
                  onChange={({ startDate, endDate, rangeType }) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      crm_lead_created_at_start: startDate,
                      crm_lead_created_at_end: endDate,
                      crm_lead_created_at_range_type: rangeType || "between",
                    }))
                  }
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag"
              width="w-full"
              placeholder="Select Tag"
              value={localFilters.tag}
              onChange={(e) => setField("tag", e.target.value)}
              options={dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 2</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag2"
              width="w-full"
              placeholder="Select Tag 2"
              value={localFilters.tag2}
              onChange={(e) => setField("tag2", e.target.value)}
              options={dummyAdvancedFilterData.tags2 || dummyAdvancedFilterData.tag2 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 3</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag3"
              width="w-full"
              placeholder="Select Tag 3"
              value={localFilters.tag3}
              onChange={(e) => setField("tag3", e.target.value)}
              options={dummyAdvancedFilterData.tags3 || dummyAdvancedFilterData.tag3 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 4</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag4"
              width="w-full"
              placeholder="Select Tag 4"
              value={localFilters.tag4}
              onChange={(e) => setField("tag4", e.target.value)}
              options={dummyAdvancedFilterData.tags4 || dummyAdvancedFilterData.tag4 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 5</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag5"
              width="w-full"
              placeholder="Select Tag 5"
              value={localFilters.tag5}
              onChange={(e) => setField("tag5", e.target.value)}
              options={dummyAdvancedFilterData.tags5 || dummyAdvancedFilterData.tag5 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 6</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag6"
              width="w-full"
              placeholder="Select Tag 6"
              value={localFilters.tag6}
              onChange={(e) => setField("tag6", e.target.value)}
              options={dummyAdvancedFilterData.tags6 || dummyAdvancedFilterData.tag6 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 7</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag7"
              width="w-full"
              placeholder="Select Tag 7"
              value={localFilters.tag7}
              onChange={(e) => setField("tag7", e.target.value)}
              options={dummyAdvancedFilterData.tags7 || dummyAdvancedFilterData.tag7 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 8</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag8"
              width="w-full"
              placeholder="Select Tag 8"
              value={localFilters.tag8}
              onChange={(e) => setField("tag8", e.target.value)}
              options={dummyAdvancedFilterData.tags8 || dummyAdvancedFilterData.tag8 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 9</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag9"
              width="w-full"
              placeholder="Select Tag 9"
              value={localFilters.tag9}
              onChange={(e) => setField("tag9", e.target.value)}
              options={dummyAdvancedFilterData.tags9 || dummyAdvancedFilterData.tag9 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 10</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag10"
              width="w-full"
              placeholder="Select Tag 10"
              value={localFilters.tag10}
              onChange={(e) => setField("tag10", e.target.value)}
              options={dummyAdvancedFilterData.tags10 || dummyAdvancedFilterData.tag10 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 11</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag11"
              width="w-full"
              placeholder="Select Tag 11"
              value={localFilters.tag11}
              onChange={(e) => setField("tag11", e.target.value)}
              options={dummyAdvancedFilterData.tags11 || dummyAdvancedFilterData.tag11 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 12</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag12"
              width="w-full"
              placeholder="Select Tag 12"
              value={localFilters.tag12}
              onChange={(e) => setField("tag12", e.target.value)}
              options={dummyAdvancedFilterData.tags12 || dummyAdvancedFilterData.tag12 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 13</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag13"
              width="w-full"
              placeholder="Select Tag 13"
              value={localFilters.tag13}
              onChange={(e) => setField("tag13", e.target.value)}
              options={dummyAdvancedFilterData.tags13 || dummyAdvancedFilterData.tag13 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 14</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag14"
              width="w-full"
              placeholder="Select Tag 14"
              value={localFilters.tag14}
              onChange={(e) => setField("tag14", e.target.value)}
              options={dummyAdvancedFilterData.tags14 || dummyAdvancedFilterData.tag14 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 15</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag15"
              width="w-full"
              placeholder="Select Tag 15"
              value={localFilters.tag15}
              onChange={(e) => setField("tag15", e.target.value)}
              options={dummyAdvancedFilterData.tags15 || dummyAdvancedFilterData.tag15 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 16</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag16"
              width="w-full"
              placeholder="Select Tag 16"
              value={localFilters.tag16}
              onChange={(e) => setField("tag16", e.target.value)}
              options={dummyAdvancedFilterData.tags16 || dummyAdvancedFilterData.tag16 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 17</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag17"
              width="w-full"
              placeholder="Select Tag 17"
              value={localFilters.tag17}
              onChange={(e) => setField("tag17", e.target.value)}
              options={dummyAdvancedFilterData.tags17 || dummyAdvancedFilterData.tag17 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 18</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag18"
              width="w-full"
              placeholder="Select Tag 18"
              value={localFilters.tag18}
              onChange={(e) => setField("tag18", e.target.value)}
              options={dummyAdvancedFilterData.tags18 || dummyAdvancedFilterData.tag18 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 19</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag19"
              width="w-full"
              placeholder="Select Tag 19"
              value={localFilters.tag19}
              onChange={(e) => setField("tag19", e.target.value)}
              options={dummyAdvancedFilterData.tags19 || dummyAdvancedFilterData.tag19 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 20</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag20"
              width="w-full"
              placeholder="Select Tag 20"
              value={localFilters.tag20}
              onChange={(e) => setField("tag20", e.target.value)}
              options={dummyAdvancedFilterData.tags20 || dummyAdvancedFilterData.tag20 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 21</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag21"
              width="w-full"
              placeholder="Select Tag 21"
              value={localFilters.tag21}
              onChange={(e) => setField("tag21", e.target.value)}
              options={dummyAdvancedFilterData.tags21 || dummyAdvancedFilterData.tag21 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 22</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag22"
              width="w-full"
              placeholder="Select Tag 22"
              value={localFilters.tag22}
              onChange={(e) => setField("tag22", e.target.value)}
              options={dummyAdvancedFilterData.tags22 || dummyAdvancedFilterData.tag22 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 23</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag23"
              width="w-full"
              placeholder="Select Tag 23"
              value={localFilters.tag23}
              onChange={(e) => setField("tag23", e.target.value)}
              options={dummyAdvancedFilterData.tags23 || dummyAdvancedFilterData.tag23 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 24</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag24"
              width="w-full"
              placeholder="Select Tag 24"
              value={localFilters.tag24}
              onChange={(e) => setField("tag24", e.target.value)}
              options={dummyAdvancedFilterData.tags24 || dummyAdvancedFilterData.tag24 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 25</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag25"
              width="w-full"
              placeholder="Select Tag 25"
              value={localFilters.tag25}
              onChange={(e) => setField("tag25", e.target.value)}
              options={dummyAdvancedFilterData.tags25 || dummyAdvancedFilterData.tag25 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 26</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag26"
              width="w-full"
              placeholder="Select Tag 26"
              value={localFilters.tag26}
              onChange={(e) => setField("tag26", e.target.value)}
              options={dummyAdvancedFilterData.tags26 || dummyAdvancedFilterData.tag26 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 27</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag27"
              width="w-full"
              placeholder="Select Tag 27"
              value={localFilters.tag27}
              onChange={(e) => setField("tag27", e.target.value)}
              options={dummyAdvancedFilterData.tags27 || dummyAdvancedFilterData.tag27 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 28</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag28"
              width="w-full"
              placeholder="Select Tag 28"
              value={localFilters.tag28}
              onChange={(e) => setField("tag28", e.target.value)}
              options={dummyAdvancedFilterData.tags28 || dummyAdvancedFilterData.tag28 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 29</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag29"
              width="w-full"
              placeholder="Select Tag 29"
              value={localFilters.tag29}
              onChange={(e) => setField("tag29", e.target.value)}
              options={dummyAdvancedFilterData.tags29 || dummyAdvancedFilterData.tag29 || dummyAdvancedFilterData.tags}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tag 30</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="tag30"
              width="w-full"
              placeholder="Select Tag 30"
              value={localFilters.tag30}
              onChange={(e) => setField("tag30", e.target.value)}
              options={dummyAdvancedFilterData.tags30 || dummyAdvancedFilterData.tag30 || dummyAdvancedFilterData.tags}
            />
          </div>

        </div>}

          {activeTab === "lead_quality" && <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Conversation Potential</label>
            <AdvancedFilterDropdown
              multiSelectFields={multiSelectFields}
              fieldKey="conversationPotential"
              width="w-full"
              placeholder="Select Potential"
              value={localFilters.conversationPotential}
              onChange={(e) => setField("conversationPotential", e.target.value)}
              options={dummyAdvancedFilterData.conversation_potential}
            />
          </div>
          </div>}

          {activeTab === "agent_feedback" && <div className="p-2 rounded-md">
                <div className="m-4 p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-2">Overall Score (0-100)</label>
                  <div className="flex items-center space-x-4 justify-between">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={localFilters.scoreLower}
                      onChange={(e) => setField("scoreLower", e.target.value === "" ? null : e.target.value)}
                      className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={localFilters.scoreUpper}
                      onChange={(e) => setField("scoreUpper", e.target.value === "" ? null : e.target.value)}
                      className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="m-4 p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-2">Fatal</label>
                  <AdvancedFilterDropdown
                    multiSelectFields={multiSelectFields}
                    fieldKey="fatal"
                    width="w-full"
                    placeholder="Select Type"
                    value={localFilters.fatal}
                    onChange={(e) => setField("fatal", e.target.value)}
                    options={["Yes", "No"]}
                  />
                </div>
                {showNeedsReview && (
                  <div className="m-4 p-2 border border-gray-200 rounded-md bg-gray-100">
                    <label className="block text-sm font-medium mb-2">Needs Review</label>
                    <AdvancedFilterDropdown
                      multiSelectFields={multiSelectFields}
                      fieldKey="needsReview"
                      width="w-full"
                      placeholder="Select Type"
                      value={localFilters.needsReview}
                      onChange={(e) => setField("needsReview", e.target.value)}
                      options={["Yes", "No"]}
                    />
                  </div>
                )}
                {(localFilters.agent_feedback_multiple || []).map((item, index) => (
                  <div key={`af-${index}`} className="m-4 flex flex-col items-center p-2 border border-gray-200 rounded-md bg-gray-100">
                    <div className="mb-2 w-full">
                      <label className="block text-sm font-medium mb-6 flex items-center justify-between">
                        Agent Feedback Parameter
                        <button
                          className="px-2 py-1 rounded-md"
                          onClick={() => removeAgentFeedbackRow(index)}
                        >
                          <FaTimes />
                        </button>
                      </label>
                      <ModernTimeDropdown
                        width="w-full"
                        placeholder="Select Parameter"
                        value={item.agent_feedback_parameter}
                        onChange={(e) => updateAgentFeedbackLocal(index, "agent_feedback_parameter", e.target.value)}
                        options={dummyAdvancedFilterData.agent_feedback_parameters}
                      />
                    </div>
                    {item.agent_feedback_parameter !== "" && (
                      <div className="mb-2 w-full">
                        <label className="block text-sm font-medium mb-2">Parameter Score (0-100)</label>
                        <div className="flex items-center space-x-4 justify-between w-full">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={item.agent_feedback_parameter_score_lower}
                            onChange={(e) => updateAgentFeedbackLocal(index, "agent_feedback_parameter_score_lower", e.target.value)}
                            className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
                            placeholder="Min"
                          />
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={item.agent_feedback_parameter_score_upper}
                            onChange={(e) => updateAgentFeedbackLocal(index, "agent_feedback_parameter_score_upper", e.target.value)}
                            className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full"
                            placeholder="Max"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-end mx-4">
                  <button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md text-sm"
                    onClick={addAgentFeedbackRow}
                  >
                    + Add More
                  </button>
                </div>
              </div>}

          {activeTab === "business_insights" && <div className="p-2 rounded-md">
                {(localFilters.business_insights_multiple || []).map((item, index) => (
                  <div key={`bi-${index}`} className="m-4 flex flex-col items-center p-2 border border-gray-200 rounded-md bg-gray-100">
                    <div className="mb-2 w-full">
                      <label className="block text-sm font-medium mb-6 flex items-center justify-between">
                        Business Insights Parameter
                        <button
                          className="px-2 py-1 rounded-md"
                          onClick={() => removeBusinessInsightRow(index)}
                        >
                          <FaTimes />
                        </button>
                      </label>
                      <ModernTimeDropdown
                        width="w-full"
                        placeholder="Select Parameter"
                        value={item.business_insights_parameter}
                        onChange={(e) => handleBusinessInsightParameterChange(index, e.target.value)}
                        options={getBusinessInsightKeys()}
                      />
                    </div>
                    {hasSelection(item.business_insights_parameter) && (
                      <div className="mb-2 w-full">
                        <label className="block text-sm font-medium mb-2">Parameter Value</label>
                        {(() => {
                          const { dataType, options } = getModuleParameterMeta("business_insight", item.business_insights_parameter);
                          const inputKind = getValueInputKind(dataType);
                          const dropdownOptions =
                            options.length > 0
                              ? options
                              : getDefaultOptionsByType(dataType).length > 0
                              ? getDefaultOptionsByType(dataType)
                              : getMergedDependentOptions(item.business_insights_parameter, getBusinessInsightValues);

                          if (inputKind === "number") {
                            const currentValue =
                              item.business_insights_parameter_value && typeof item.business_insights_parameter_value === "object"
                                ? item.business_insights_parameter_value
                                : { lower: "", upper: "" };
                            return (
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  value={currentValue.lower ?? ""}
                                  placeholder="Lower Limit"
                                  onChange={(e) =>
                                    updateBusinessInsightsLocal(index, "business_insights_parameter_value", {
                                      lower: e.target.value,
                                      upper: currentValue.upper ?? "",
                                    })
                                  }
                                  className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                                />
                                <input
                                  type="number"
                                  value={currentValue.upper ?? ""}
                                  placeholder="Upper Limit"
                                  onChange={(e) =>
                                    updateBusinessInsightsLocal(index, "business_insights_parameter_value", {
                                      lower: currentValue.lower ?? "",
                                      upper: e.target.value,
                                    })
                                  }
                                  className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                                />
                              </div>
                            );
                          }

                          if (inputKind === "dropdown") {
                            return (
                              <AdvancedFilterDropdown
                                width="w-full"
                                fieldKey="business_insights_parameter_value"
                                aliasKeys={["business_insights_parameter_value", "business_insight_parameter_value", "business_insights"]}
                                multiSelectFields={multiSelectFields}
                                placeholder="Select Value"
                                value={item.business_insights_parameter_value}
                                onChange={(e) => updateBusinessInsightsLocal(index, "business_insights_parameter_value", e.target.value)}
                                options={dropdownOptions}
                              />
                            );
                          }

                          return (
                            <input
                              type="text"
                              value={item.business_insights_parameter_value ?? ""}
                              placeholder="Enter Value"
                              onChange={(e) => updateBusinessInsightsLocal(index, "business_insights_parameter_value", e.target.value)}
                              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                            />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-end mx-4">
                  <button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md text-sm"
                    onClick={addBusinessInsightRow}
                  >
                    + Add More
                  </button>
                </div>
              </div>}

          {activeTab === "concerns" && <div className="p-2 rounded-md">
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Open Concerns & Queries</label>
                  <AdvancedFilterDropdown
                    multiSelectFields={multiSelectFields}
                    fieldKey="openConcerns"
                    width="w-full"
                    placeholder="Select Type"
                    value={localFilters.openConcerns}
                    onChange={(e) => setField("openConcerns", e.target.value)}
                    options={dummyAdvancedFilterData.open_concerns_and_queries}
                  />
                </div>
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Concern Category</label>
                  <AdvancedFilterDropdown
                    width="w-full"
                    fieldKey="concernCategory"
                    multiSelectFields={multiSelectFields}
                    placeholder="Select Concern Category"
                    value={localFilters.concernCategory}
                    onChange={(e) => setLocalFilters((prev) => ({ ...prev, concernCategory: e.target.value, concernSubcategory: "" }))}
                    options={getConcernCategoryKeys()}
                  />
                </div>
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Concern Subcategory</label>
                  <AdvancedFilterDropdown
                    multiSelectFields={multiSelectFields}
                    fieldKey="concernSubcategory"
                    width="w-full"
                    placeholder="Select Concern Subcat..."
                    value={localFilters.concernSubcategory}
                    onChange={(e) => setField("concernSubcategory", e.target.value)}
                    options={getMergedDependentOptions(localFilters.concernCategory, getConcernSubcategoriesForCategory)}
                  />
                </div>
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Query Category</label>
                  <AdvancedFilterDropdown
                    width="w-full"
                    fieldKey="queryCategory"
                    multiSelectFields={multiSelectFields}
                    placeholder="Select Query Category"
                    value={localFilters.queryCategory}
                    onChange={(e) => setLocalFilters((prev) => ({ ...prev, queryCategory: e.target.value, querySubcategory: "" }))}
                    options={getQueryCategoryKeys()}
                  />
                </div>
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Query Subcategory</label>
                  <AdvancedFilterDropdown
                    multiSelectFields={multiSelectFields}
                    fieldKey="querySubcategory"
                    width="w-full"
                    placeholder="Select Query Subcategory"
                    value={localFilters.querySubcategory}
                    onChange={(e) => setField("querySubcategory", e.target.value)}
                    options={getMergedDependentOptions(localFilters.queryCategory, getQuerySubcategoriesForCategory)}
                  />
                </div>
              </div>}

          {activeTab === "disposition" && <div className="p-2 rounded-md">
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Disposition</label>
                  <AdvancedFilterDropdown
                    width="w-full"
                    fieldKey="disposition"
                    multiSelectFields={multiSelectFields}
                    placeholder="Select Disposition"
                    value={localFilters.disposition}
                    onChange={(e) => setLocalFilters((prev) => ({ ...prev, disposition: e.target.value, subdisposition: "" }))}
                    options={getDispositionKeys()}
                  />
                </div>
                <div className="m-4 flex flex-col items-start p-2 border border-gray-200 rounded-md bg-gray-100">
                  <label className="block text-sm font-medium mb-6">Subdisposition</label>
                  <AdvancedFilterDropdown
                    multiSelectFields={multiSelectFields}
                    fieldKey="subdisposition"
                    width="w-full"
                    placeholder="Select Subdisposition"
                    value={localFilters.subdisposition}
                    onChange={(e) => setField("subdisposition", e.target.value)}
                    options={getMergedDependentOptions(localFilters.disposition, getSubdispositionsForDisposition)}
                  />
                </div>
              </div>}

          {activeTab === "custom_module" && <div className="p-2 rounded-md">
                {(localFilters.custom_module_multiple || []).map((item, index) => (
                  <div key={`cm-${index}`} className="m-4 flex flex-col items-center p-2 border border-gray-200 rounded-md bg-gray-100">
                    <div className="mb-2 w-full">
                      <label className="block text-sm font-medium mb-6 flex items-center justify-between">
                        Parameter
                        <button className="px-2 py-1 rounded-md" onClick={() => removeCustomModuleRow(index)}>
                          <FaTimes />
                        </button>
                      </label>
                      <ModernTimeDropdown
                        width="w-full"
                        placeholder="Select Parameter"
                        value={item.custom_module_parameter_name}
                        onChange={(e) => handleCustomModuleParameterChangeLocal(index, e.target.value)}
                        options={getCustomModuleKeys()}
                      />
                    </div>
                    <div className="mb-2 w-full">
                      <label className="block text-sm font-medium mb-2">Value</label>
                      {(() => {
                        const { dataType, options } = getModuleParameterMeta("custom_module", item.custom_module_parameter_name);
                        const inputKind = getValueInputKind(dataType);
                        const dropdownOptions =
                          options.length > 0
                            ? options
                            : getDefaultOptionsByType(dataType).length > 0
                            ? getDefaultOptionsByType(dataType)
                            : getMergedDependentOptions(item.custom_module_parameter_name, getCustomModuleValues);

                        if (inputKind === "number") {
                          const currentValue =
                            item.custom_module_parameter_answer && typeof item.custom_module_parameter_answer === "object"
                              ? item.custom_module_parameter_answer
                              : { lower: "", upper: "" };
                          return (
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="number"
                                value={currentValue.lower ?? ""}
                                placeholder="Lower Limit"
                                onChange={(e) =>
                                  updateCustomModuleLocal(index, "custom_module_parameter_answer", {
                                    lower: e.target.value,
                                    upper: currentValue.upper ?? "",
                                  })
                                }
                                className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                              />
                              <input
                                type="number"
                                value={currentValue.upper ?? ""}
                                placeholder="Upper Limit"
                                onChange={(e) =>
                                  updateCustomModuleLocal(index, "custom_module_parameter_answer", {
                                    lower: currentValue.lower ?? "",
                                    upper: e.target.value,
                                  })
                                }
                                className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                              />
                            </div>
                          );
                        }

                        if (inputKind === "dropdown") {
                          return (
                            <AdvancedFilterDropdown
                              multiSelectFields={multiSelectFields}
                              fieldKey="custom_module_parameter_answer"
                              aliasKeys={["custom_module_parameter_answer", "custom_module"]}
                              width="w-full"
                              placeholder="Select Value"
                              value={item.custom_module_parameter_answer}
                              onChange={(e) => updateCustomModuleLocal(index, "custom_module_parameter_answer", e.target.value)}
                              options={dropdownOptions}
                            />
                          );
                        }

                        return (
                          <input
                            type="text"
                            value={item.custom_module_parameter_answer ?? ""}
                            placeholder="Enter Value"
                            onChange={(e) => updateCustomModuleLocal(index, "custom_module_parameter_answer", e.target.value)}
                            className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:outline-none global-input w-full"
                          />
                        );
                      })()}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-end mx-4">
                  <button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md text-sm"
                    onClick={addCustomModuleRow}
                  >
                    + Add More
                  </button>
                </div>
              </div>}
        </div>
        <div className="border-t px-4 py-3 flex items-center justify-between gap-3 bg-white" style={{borderRadius: "0 0 10px 10px"}}>
          <div className="flex-1 min-w-0 flex items-center gap-2 overflow-x-auto">
            {selectedFilterChips.length === 0 ? (
              <span className="text-xs text-gray-500">No filters selected</span>
            ) : (
              selectedFilterChips.map((chip) => (
                <div
                  key={chip.id}
                  className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-md px-2 py-1 text-xs whitespace-nowrap"
                >
                  <span>{chip.label}</span>
                  <button className="text-indigo-700 hover:text-indigo-900" onClick={chip.onRemove}>
                    <FaTimes />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-md" onClick={handleClear}>Clear</button>
            <button className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}
